import {useCallback, useEffect, useMemo, useState} from "react";
import {Grid, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {MessageHeader} from "../MessagesBaseComponent/MessageHeader";
import {withRouter} from "react-router";
import moment from "moment";
import * as PropTypes from "prop-types";
import messages from "containers/PatientDataPage/messages";
import clsx from "clsx";
import MessagePatient from "components/Message/MessagePatient";
import SimplePagination from "basicComponents/SimplePagination";
import {useDebounce} from "use-debounce";
import useFormatMessage from "hooks/intl/useFormatMessage";
import OrderIcon from "basicComponents/Icons/OrderIcon";
import CalendarIcon from "basicComponents/Icons/CalendarIcon";
import MessageIcon from "basicComponents/Icons/MessageIcon";
import NotificationIcon from "basicComponents/Icons/NotificationIcon";

const useStyles = makeStyles(theme => ({
  // Wrapper
  wrapper: {
    width: "100%",
    padding: "0px 40px 20px 40px",
    display: "flex",
    alignItems: "start",
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  contentContainer: {
    flex: 1,
    marginTop: "61px",
    marginLeft: "-1px",
    minWidth: 0,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    flex: "0.5",
    height: "100%",
    width: "100%",
  },
  // LeftOrderBox
  patientBox: {
    flex: 1,
    border: "1px solid rgba(0,0,0,0.24)",
    overflow: "hidden",
    borderRadius: "0 0 10px 10px",
    minWidth: "300px",
    height: ({ fullHeight }) => `${fullHeight}px`,
    textOverflow: "ellipsis",
  },
  headerWrapper: {
    flex: 1,
    border: "1px solid rgba(0,0,0,0.24)",
    borderBottom: "none",
    borderRadius: "10px 10px 0 0",
    display: "flex",
    minWidth: "300px",
    flexDirection: "column",
  },
  label: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid rgba(0,0,0,0.24)",
    padding: theme.spacing(0, 2),
  },
  wrapperContainer: {
    display: "flex",
    flexWrap: "wrap",
  },
  messageWrapper: {
    padding: "5px",
    cursor: "pointer",
  },
  badge: {
    backgroundColor: "#ff6a55",
    borderRadius: "50%",
    color: "#ffffff",
    height: "1.5rem",
    width: "1.5rem",
    textAlign: "center",
    marginLeft: "5px",
    fontSize: "16px",
    border: "solid 1px rgb(0,0,0,0.24)",
    lineHeight: "20px",
  },
  // Inner Label
  pagination: { display: "flex", flex: 1, justifyContent: "center" },
  content: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2),
  },
  divider: {
    backgroundColor: "rgba(0,0,0,0.24)",
    height: "1px",
    margin: theme.spacing(0, 2),
  },
  messageText: {
    fontSize: "14px",
    color: "inherit",
    flex: "0.8",
  },
  row: {
    height: ({ rowHeight }) => `${rowHeight}px`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: theme.spacing(0, 2),
    borderBottom: "1px solid rgba(0,0,0,0.24)",
    cursor: "pointer",
    padding: theme.spacing(0, 1),
    outline: "none",

    "&:nth-child(20)": {
      borderBottom: "none",
    },

    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.70)",
      color: "white",
    },

    "&.done": {
      backgroundColor: "rgba(242,241,239,1)",
      color: "black",
    },
    "&.active": {
      backgroundColor: "rgba(0,0,0,0.70)",
      color: "white",
    },
  },
}));

// height of Row
const rowHeight = 40;
// container height
const fullHeight = 800;
const limit = 20;

function PatientMessage({
  patient = {},
  history,
  isLoading = false,
  messageId,
  activeMessage,
  onCloseMessage,
  patientMessages = [],
  onGetMessages,
  total = 0,
  currentPage,
  onSetCurrentPage,
  onRemakeRequest,
  remakeProductOrder,
  onResetRemakeProductOrder,
  statusOpenCount,
  onRefittingRequest,
  reFittingPlannedTreatment,
  onResetRefittingRequest
}) {
  const [filter, setFilter] = useState("");
  const [status, setStatus] = useState(1);
  const [debouncedFilter] = useDebounce(filter, 300);
  const [debouncedStatus] = useDebounce(status, 300);
  const [debouncedCurrentPage] = useDebounce(currentPage, 300);

  useEffect(
    () => {
      if (patient && patient.id) {
        onGetMessages({
          patientId: patient.id,
          status: debouncedStatus,
          filter: debouncedFilter,
          limit,
          offset: debouncedCurrentPage * limit,
        });
      }
    },
    [
      debouncedCurrentPage,
      debouncedFilter,
      onGetMessages,
      patient,
      debouncedStatus,
      remakeProductOrder,
    ],
  );

  const classes = useStyles({ rowHeight, fullHeight });
  const messageAll = useFormatMessage(messages.message_type_all);
  const messageOrders = useFormatMessage(messages.message_type_orders);
  const messageAppointment = useFormatMessage(
    messages.message_type_appointments,
  );
  const messageOther = useFormatMessage(messages.message_type_other);
  const messageNotification = useFormatMessage(
    messages.message_type_notifications,
  );

  const options = useMemo(
    () => [
      {
        label: messageAll,
        value: 1,
        id: "ALL",
      },
      {
        label: messageOrders,
        value: 2,
        id: "ORDERS",
        svg: <OrderIcon />,
      },
      {
        label: messageAppointment,
        value: 3,
        id: "APPOINTMENTS",
        svg: <CalendarIcon />,
      },
      {
        label: messageOther,
        value: 4,
        id: "OTHER",
        svg: <MessageIcon />,
      },
      {
        label: messageNotification,
        value: 5,
        id: "NOTIFICATIONS",
        svg: (
          <div className={classes.badge} data-cy="badgeMessages">
            {statusOpenCount || "0"}
          </div>
        ),
      },
    ],
    [
      messageAll,
      messageOrders,
      messageAppointment,
      messageOther,
      messageNotification,
      classes.badge,
      statusOpenCount,
    ],
  );

  useEffect(
    () => {
      onSetCurrentPage(0);
    },
    [debouncedFilter, debouncedStatus, onSetCurrentPage],
  );

  const handleRowClick = useCallback(
    mid => () => {
      if (patient && patient.id) {
        history.push(`/patient/${patient.id}/${mid}`);
      }
    },
    [patient, history],
  );

  const getMessageIcon = useCallback(filterType => {
    switch (filterType) {
      case 2:
        return <OrderIcon />;
      case 3:
        return <CalendarIcon />;
      case 4:
        return <MessageIcon />;
      case 5:
        return <NotificationIcon />;
      default:
        return null;
    }
  }, []);

  // here
  const renderPatientMessage = useCallback(
    (message, index) => {
      var { topic,createdAtAtString, updatedAt, id, filterType, latestMessageId } = message;
      if (!patient) {
        return null;
      }
      // 2 hour dif bug here
      const dateFormat = moment(message.updatedAtString.replace('UTC','')).fromNow();
      // const dateFormat = moment.unix(updatedAt).fromNow();
      // const dateFormat = moment(createdAtAtString.slice(0,createdAtAtString.length - 4)).fromNow();
      return (
        <div
          style={{background:'white',color:'black'}}
          key={id}
          tabIndex={index}
          role="button"
          className={clsx(classes.row, {
            active: parseInt(id, 10) === parseInt(messageId, 10),
            done: latestMessageId !== id
          })}
          onClick={handleRowClick(id)}
        >
          <Typography color="inherit" noWrap className={classes.messageText}>
            <span style={{ marginRight: "10px" }}>
              {getMessageIcon(filterType)}
            </span>

            {topic}
          </Typography>
          <div>{dateFormat}</div>
        </div>
      );
    },
    [
      classes.messageText,
      classes.row,
      handleRowClick,
      messageId,
      patient,
      getMessageIcon,
    ],
  );

  const onClose = useCallback(
    () => {
      if (patient) {
        history.push(`/patient/${patient.id}`);
      }
    },
    [history, patient],
  );

  const handleCloseMessage = useCallback(
    mid => {
      onCloseMessage(mid);
      // if (patient && patient.id) {
      //   history.push(`/patient/${patient.id}`);
      // }
    },
    [onCloseMessage],
  );

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <div className={classes.contentWrapper}>
            <div style={{ display: "flex" }}>
              <div style={{background:'white'}} className={classes.headerWrapper}>
                <MessageHeader
                  filter={filter}
                  onFilterChange={setFilter}
                  status={status}
                  onStatusChange={newStatus => {
                    setStatus(newStatus);
                    history.push(`/patient/${patient.id}`);
                  }}
                  options={options}
                />
              </div>
            </div>
            <div style={{background:'white'}} className={classes.wrapperContainer}>
              <div className={classes.patientBox} data-cy="patientMessages">
                {/* here is the loop, somehow inject the old productplans */}
                
                {(() => {
                  
                  
                  if(window.globalAllProductPlans == null){
                    return
                  }
                  if(patientMessages.length == 0){
                    return
                  }
                  var results = []
                  
                  for(var pp of window.globalAllProductPlans){
                    pp.type='productplan'
                    pp.createdAtUnix = moment(pp.createdAt).unix()
                  }
                  for(var pm of patientMessages){
                    pm.type = 'patientmessage'
                    pm.createdAtUnix = pm.createdAt
                  }
                  var allitems = [...window.globalAllProductPlans,...patientMessages]
                  allitems.sort((a,b) => {
                    return b.createdAtUnix - a.createdAtUnix
                  })
                  var  i = 0;
                  for(let item of allitems){
                    let pp = item
                    if(item.type == 'productplan'){
                      results.push(<div style={{display:"flex",gap:"10px",alignItems:"center",padding:"0 8px",margin:"0 16px", height:"39px",borderBottom:"1px solid grey"}}>
                        <OrderIcon />
                        <a href={`/productplan/${pp.id}`} onClick={(e) => {
                          e.preventDefault()
                          getPaul('productOrder',"productPlanId",pp.id,(data) => {
                            location.href = `/order/patient/${patient.id}/treatment/${last(data).id}/summary?readonly=true`
                          })
                        }}>Order aangemaakt {moment(pp.createdAt).format("D MMM YYYY")}</a>
                      </div>)
                    }else if(item.type == 'patientmessage'){
                      results.push(renderPatientMessage(item,i))
                    }
                    i++
                  }

                  return results
                })()}
                {/* {patientMessages.map(renderPatientMessage)} */}
              </div>
            </div>

            <div className={classes.pagination}>
              <SimplePagination
                onChange={page => {
                  onSetCurrentPage(page);
                  history.push(`/patient/${patient.id}`);
                }}
                currentPage={currentPage}
                limit={limit}
                total={total}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <div className={classes.contentContainer}>
            {activeMessage && (
              <MessagePatient
                onRefittingRequest={onRefittingRequest}
                onResetRefittingRequest={onResetRefittingRequest}
                reFittingPlannedTreatment={reFittingPlannedTreatment}
                onCloseMessage={handleCloseMessage}
                onClose={onClose}
                onRemakeRequest={onRemakeRequest}
                activeMessage={activeMessage}
                isLoading={isLoading}
                remakeProductOrder={remakeProductOrder}
                onResetRemakeProductOrder={onResetRemakeProductOrder}
              />
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
}

PatientMessage.propTypes = {
  patient: PropTypes.object,
  practice: PropTypes.object,
  isLoading: PropTypes.bool,
  messageId: PropTypes.string,
  activeMessage: PropTypes.object,
  onCloseMessage: PropTypes.func.isRequired,
  onGetMessages: PropTypes.func.isRequired,
  patientMessages: PropTypes.array,
  total: PropTypes.number,
  statusOpenCount: PropTypes.number,
  currentPage: PropTypes.number,
  onSetCurrentPage: PropTypes.func.isRequired,
  onRemakeRequest: PropTypes.func.isRequired,
  remakeProductOrder: PropTypes.object,
  reFittingPlannedTreatment: PropTypes.object,
  onRefittingRequest: PropTypes.func,
  onResetRefittingRequest: PropTypes.func,
  onResetRemakeProductOrder: PropTypes.func,
};

export default withRouter(PatientMessage);

function last(arr){
  return arr[arr.length - 1]
}